<template>
  <div class="space-y-4 bg-surface-grey-light rounded-md">
    <div
      class="bg-surface-light p-4 border-surface-grey rounded-sm flex items-start justify-between gap-4"
    >
      <div class="space-y-1 min-w-0">
        <div class="font-semibold text-sm truncate">{{ getLocalizedCartItemValue(item, locale, 'info1') }}</div>

        <div v-if="item.eventtime || item.eventtime_override" class="text-xs text-text capitalize truncate">
          {{ getDisplayDate(item, locale) }}
        </div>

        <div class="text-xs text-text truncate">{{ getLocalizedCartItemValue(item, locale, 'venuename') }}</div>
      </div>
      <img
        :src="photoToUrl(item.photo1_id)"
        :alt="item.name"
        class="w-25 aspect-100/56 rounded-sm object-cover border border-surface-grey"
      />
    </div>
    <!-- Tickets -->
    <CheckoutItemSection
      :heading="$t('checkout.tickets').toUpperCase()"
      :items="getTickets(item)"
      :id="item.id"
      type="ticket"
      :show-edit="showTicketEdit"
      :show-remove="showRemove"
    />
    <!-- Add-ons -->
    <CheckoutItemSection
      :id="item.id"
      :heading="$t('checkout.addons').toUpperCase()"
      :items="getUpsaleTickets(item)"
      type="upsaleTicket"
      :show-edit="showUpsaleEdit"
      :show-remove="showRemove"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useRoute } from 'vue-router';

  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useDoorsaleStore } from '@/stores/doorsaleStore';
  import type { CartItem } from '@/types';
  import { getDisplayDate, getLocalizedCartItemValue, getTickets, getUpsaleTickets } from '@/utils/cartItemUtil';
  import { photoToUrl } from '@/utils/photoToUrl';

  import CheckoutItemSection from './CheckoutItemSection.vue';

  const { locale } = useI18n();
  const route = useRoute();
  const checkoutStore = useCheckoutStore();
  const doorsaleStore = useDoorsaleStore();

  const props = defineProps<{
    item: CartItem
  }>();

  // On doorsale routes, hide edit for the active event and all events that come after it
  const hideEditOnDoorsale = computed(() => {
    if (!route.name?.toString().startsWith('doorsale-')) return false;
    const currentIndex = doorsaleStore.selectedEventIds.findIndex(id => id == checkoutStore.getCheckoutID());
    const itemIndex = doorsaleStore.selectedEventIds.findIndex(id => id == props.item.id);
    return currentIndex !== -1 && itemIndex >= currentIndex;
  });

  const showTicketEdit = computed(() =>
    route.name !== 'checkout-receipt' &&
    route.name !== 'checkout-payment' &&
    route.name !== 'doorsale-receipt' &&
    !hideEditOnDoorsale.value &&
    !(route.name === 'checkout-tickets' && checkoutStore.getCheckoutID() == props.item.id) &&
    !(route.name === 'doorsale-tickets' && checkoutStore.getCheckoutID() == props.item.id),
  );

  const showUpsaleEdit = computed(() =>
    route.name !== 'checkout-receipt' &&
    route.name !== 'checkout-payment' &&
    route.name !== 'doorsale-receipt' &&
    !hideEditOnDoorsale.value &&
    !(route.name === 'checkout-upsale' && checkoutStore.getCheckoutID() == props.item.id) &&
    !(route.name === 'doorsale-upsale' && checkoutStore.getCheckoutID() == props.item.id),
  );

  const showRemove = computed(() =>
    route.name !== 'checkout-receipt' &&
    route.name !== 'checkout-payment' &&
    route.name !== 'checkout-information' &&
    route.name !== 'checkout-upsale' &&
    route.name !== 'checkout-tickets' &&
    route.name !== 'doorsale-receipt' &&
    route.name !== 'doorsale-information' &&
    route.name !== 'doorsale-upsale' &&
    route.name !== 'doorsale-tickets',
  );
</script>
